import React from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AnnouncementI } from "../../../types";
import { AnnouncementStatusFilter } from "../../../api/types";
import Button from "../../../componentLibrary/Button";
import { useGetAllAnnouncementsForAdmin } from "../../../api/queries";
import AnnouncementCard from "./AnnouncementsCard";

interface AnnouncementsListProps {
  announcements: AnnouncementI[];
  announcementId?: string;
  setAnnouncementData: (announcement: AnnouncementI | undefined) => void;
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
  statusFilter: AnnouncementStatusFilter;
  setStatusFilter: (filter: AnnouncementStatusFilter) => void;
}

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({
  announcements,
  announcementId,
  setAnnouncementData,
  setIsEdit,
  statusFilter,
  setStatusFilter,
}) => {
  // All these hooks use showDismissed: "true" - show ALL announcements
  const { data: allAnnouncements = [] } = useGetAllAnnouncementsForAdmin("all");
  const { data: activeAnnouncements = [] } =
    useGetAllAnnouncementsForAdmin("active");
  const { data: upcomingAnnouncements = [] } =
    useGetAllAnnouncementsForAdmin("upcoming");
  const { data: expiredAnnouncements = [] } =
    useGetAllAnnouncementsForAdmin("expired");

  const handleAddNew = () => {
    setAnnouncementData(undefined);
    setIsEdit(false);
  };

  const getStatusCount = (status: AnnouncementStatusFilter) => {
    switch (status) {
      case "all":
        return allAnnouncements.length;
      case "active":
        return activeAnnouncements.length;
      case "upcoming":
        return upcomingAnnouncements.length;
      case "expired":
        return expiredAnnouncements.length;
      default:
        return 0;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Announcements</h2>
        <Button
          variant="primary"
          onClick={handleAddNew}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          New
        </Button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {(["all", "active", "upcoming", "expired"] as const).map((status) => (
          <Button
            key={status}
            onClick={() => setStatusFilter(status)}
            variant={statusFilter === status ? "dark" : "secondary"}
            size="sm"
            className="whitespace-nowrap"
          >
            {`${status.charAt(0).toUpperCase() + status.slice(1)}\u00A0(${getStatusCount(status)})`}
          </Button>
        ))}
      </div>

      {/* Announcements List */}
      <div className="space-y-2">
        {announcements.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No {statusFilter !== "all" ? statusFilter : ""} announcements found
          </div>
        ) : (
          announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement._id}
              announcement={announcement}
              isSelected={announcement._id === announcementId}
              onClick={() => {
                setAnnouncementData(announcement);
                setIsEdit(false);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AnnouncementsList;
